import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Mail, RefreshCw, AlertCircle, Check, HelpCircle, Send, AtSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EmailHistoryModal — admin-only view of every email attempt for a user.
 *
 * Merges the new `email_delivery_log` collection (per-attempt PENDING →
 * SENT/FAILED/MOCKED rows) with the legacy `welcome_email_log` (success-
 * only rows tagged 'legacy — cannot confirm delivery').
 *
 * NEVER shows a row as "Delivered" without webhook evidence — SENT means
 * the provider accepted the API call. Approval-email resend does NOT
 * change any user state.
 *
 * Prompt 2 additions:
 * - Correspondence-email inline editor (PATCH /correspondence-email).
 *   When set, nudges go there instead of the login `email`.
 * - Two manual nudge buttons (Cold Start / Silent Regular) with the
 *   same Yes/Cancel confirmation pattern used by resend.
 */
export default function EmailHistoryModal({ open, onClose, user, token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Unified action-confirm state — one of null | RESEND_WELCOME | RESEND_APPROVAL
  //   | NUDGE_COLD_START | NUDGE_SILENT_REGULAR. `busy` guards double-clicks.
  const [action, setAction] = useState({ confirming: null, busy: false, msg: null });
  const [correspondenceEmail, setCorrespondenceEmail] = useState('');
  const [savingCorr, setSavingCorr] = useState(false);

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${user.id}/email-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.history || []);
    } catch { setRows([]); }
    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    setCorrespondenceEmail(user?.correspondence_email || '');
    load();
  }, [open, user?.id]);

  if (!open) return null;

  const pill = (status) => {
    const map = {
      SENT:   { c: 'bg-emerald-100 text-emerald-800 border-emerald-300', i: <Check className="w-3 h-3" />, l: 'Sent' },
      FAILED: { c: 'bg-rose-100 text-rose-800 border-rose-300', i: <AlertCircle className="w-3 h-3" />, l: 'Failed' },
      PENDING:{ c: 'bg-amber-100 text-amber-800 border-amber-300', i: <RefreshCw className="w-3 h-3 animate-spin" />, l: 'Pending' },
      MOCKED: { c: 'bg-slate-100 text-slate-700 border-slate-300', i: <HelpCircle className="w-3 h-3" />, l: 'Mocked' },
      DELIVERED: { c: 'bg-emerald-100 text-emerald-800 border-emerald-300', i: <Check className="w-3 h-3" />, l: 'Delivered' },
    }[status] || { c: 'bg-slate-100 text-slate-700 border-slate-300', i: <HelpCircle className="w-3 h-3" />, l: status };
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${map.c}`}>
        {map.i}{map.l}
      </span>
    );
  };

  const requestAction = (kind) => setAction({ confirming: kind, busy: false, msg: null });
  const cancelAction = () => setAction({ confirming: null, busy: false, msg: null });

  const confirmAction = async () => {
    if (!action.confirming) return;
    setAction((s) => ({ ...s, busy: true, msg: null }));
    try {
      const kind = action.confirming;
      let url = null;
      if (kind === 'RESEND_WELCOME')          url = `${API}/admin/users/${user.id}/resend/WELCOME`;
      else if (kind === 'RESEND_APPROVAL')    url = `${API}/admin/users/${user.id}/resend/ACCOUNT_APPROVED`;
      else if (kind === 'NUDGE_COLD_START')   url = `${API}/admin/users/${user.id}/nudge/COLD_START`;
      else if (kind === 'NUDGE_SILENT_REGULAR') url = `${API}/admin/users/${user.id}/nudge/SILENT_REGULAR`;
      const r = await axios.post(url, { confirm: true }, { headers: { Authorization: `Bearer ${token}` } });
      const isNudge = kind.startsWith('NUDGE_');
      let msg;
      if (r.data?.success) {
        msg = isNudge
          ? `Nudge sent to ${r.data?.recipient || user.email}`
          : `Resent (id: ${r.data?.email_id?.slice?.(0, 8) || 'ok'})`;
      } else {
        msg = `Failed: ${r.data?.reason || r.data?.status}`;
      }
      setAction({ confirming: null, busy: false, msg });
      await load();
    } catch (e) {
      setAction({ confirming: null, busy: false, msg: `Error: ${e?.response?.data?.detail || 'send failed'}` });
    }
  };

  const saveCorrespondence = async () => {
    if (!user?.id) return;
    const val = correspondenceEmail.trim();
    // basic client-side validation — the backend re-validates as EmailStr
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setAction({ confirming: null, busy: false, msg: 'Please enter a valid email address.' });
      return;
    }
    setSavingCorr(true);
    try {
      await axios.patch(
        `${API}/admin/users/${user.id}/correspondence-email`,
        { correspondence_email: val || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAction({ confirming: null, busy: false, msg: val ? `Correspondence email set to ${val}` : 'Correspondence email cleared' });
    } catch (e) {
      setAction({ confirming: null, busy: false, msg: `Error: ${e?.response?.data?.detail || 'save failed'}` });
    }
    setSavingCorr(false);
  };

  const confirmLabels = {
    RESEND_WELCOME: 'Welcome',
    RESEND_APPROVAL: 'Approval',
    NUDGE_COLD_START: 'Cold Start nudge',
    NUDGE_SILENT_REGULAR: 'Silent Regular nudge',
  };
  const isNudgeConfirm = action.confirming?.startsWith('NUDGE_');
  const nudgeRecipient = correspondenceEmail.trim() || user?.email;

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="email-history-modal"
    >
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-slate-200 dark:border-slate-700 gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Mail className="w-5 h-5" /> Email History
            </h2>
            <p className="text-xs text-slate-500 mt-1 truncate">{user?.email}</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={() => requestAction('RESEND_WELCOME')}
              className="px-2.5 py-1 text-xs font-medium border border-cyan-400 text-cyan-700 hover:bg-cyan-50 rounded"
              data-testid="resend-welcome-btn"
            >
              Resend Welcome
            </button>
            {user?.is_approved && (
              <button
                type="button"
                onClick={() => requestAction('RESEND_APPROVAL')}
                className="px-2.5 py-1 text-xs font-medium border border-emerald-400 text-emerald-700 hover:bg-emerald-50 rounded"
                data-testid="resend-approval-btn"
              >
                Resend Approval
              </button>
            )}
            <button
              type="button"
              onClick={() => requestAction('NUDGE_COLD_START')}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-violet-400 text-violet-700 hover:bg-violet-50 rounded"
              data-testid="nudge-cold-start-btn"
              title="Manual engagement email for users who registered but never ran an analysis"
            >
              <Send className="w-3 h-3" /> Cold Start
            </button>
            <button
              type="button"
              onClick={() => requestAction('NUDGE_SILENT_REGULAR')}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-indigo-400 text-indigo-700 hover:bg-indigo-50 rounded"
              data-testid="nudge-silent-regular-btn"
              title="Manual check-in for users who were active and have gone quiet"
            >
              <Send className="w-3 h-3" /> Silent Regular
            </button>
            <button
              onClick={onClose}
              className="p-1 text-slate-500 hover:text-slate-900"
              data-testid="email-history-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Correspondence-email inline editor — nudge destination override */}
        <div className="mx-5 mt-3 p-3 rounded border border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 flex items-center gap-2 flex-wrap"
             data-testid="correspondence-email-row">
          <AtSign className="w-4 h-4 text-slate-500" />
          <label htmlFor="corr-email-input" className="text-xs font-semibold text-slate-700 dark:text-slate-200">Correspondence email</label>
          <input
            id="corr-email-input"
            type="email"
            value={correspondenceEmail}
            onChange={(e) => setCorrespondenceEmail(e.target.value)}
            placeholder="(leave blank to nudge login email)"
            className="flex-1 min-w-[220px] text-xs px-2 py-1 border border-slate-300 rounded bg-white dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
            data-testid="correspondence-email-input"
          />
          <button
            type="button"
            onClick={saveCorrespondence}
            disabled={savingCorr}
            className="px-2.5 py-1 text-xs font-medium bg-slate-800 hover:bg-slate-900 text-white rounded disabled:opacity-60"
            data-testid="correspondence-email-save"
          >
            {savingCorr ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </button>
          <span className="basis-full text-[11px] text-slate-500">
            When set, Cold Start and Silent Regular nudges are sent here instead of the login email. Transactional emails (Welcome/Approval/Reset) always go to the login email.
          </span>
        </div>

        {action.confirming && (
          <div className="mx-5 mt-3 p-3 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900 flex items-center justify-between gap-3" data-testid="resend-confirm-bar">
            <span>
              Send <b>{confirmLabels[action.confirming]}</b> email
              {isNudgeConfirm
                ? <> to <b>{nudgeRecipient}</b>?</>
                : <> to <b>{user?.email}</b>? A new audit row will be created — the original history stays intact.</>}
            </span>
            <span className="flex gap-2">
              <button type="button" onClick={confirmAction} disabled={action.busy}
                className="px-3 py-1 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-60"
                data-testid="resend-confirm-yes">
                {action.busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
              </button>
              <button type="button" onClick={cancelAction}
                className="px-3 py-1 text-xs bg-slate-200 hover:bg-slate-300 text-slate-800 rounded"
                data-testid="resend-confirm-cancel">Cancel</button>
            </span>
          </div>
        )}

        {action.msg && (
          <div className="mx-5 mt-3 p-2 rounded text-xs text-slate-700 bg-slate-50 border border-slate-200" data-testid="email-action-msg">
            {action.msg}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-slate-500 italic">
              No email history for this user. Legacy accounts registered before the audit collection existed may show <b>Unknown — legacy record</b> once retriggered.
            </div>
          )}
          {rows.map((row, i) => (
            <div
              key={row.id || `legacy-${i}`}
              className={`mb-2 p-3 rounded border ${row.source === 'legacy' ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white dark:bg-slate-800'}`}
              data-testid={`email-row-${i}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100">{row.email_type}</span>
                    {pill(row.status)}
                    {row.source === 'legacy' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title="Legacy welcome_email_log row — cannot confirm delivery">legacy</span>
                    )}
                    {row.triggered_by?.startsWith('admin:') && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800">admin resend</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 truncate">{row.subject}</div>
                  {row.error && <div className="text-xs text-rose-700 mt-1"><b>Error:</b> {row.error}</div>}
                  <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                    <span>To: {row.recipient_email}</span>
                    {row.provider_id && <span>id: {row.provider_id.slice(0, 12)}…</span>}
                    {row.attempted_at && <span>{new Date(row.attempted_at).toLocaleString()}</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
